import {
  DesignTokenSizeEnum,
  Text,
  TokenTextAppearance,
  TextTag,
} from '@volkswagen-onehub/components-core';
import { useTribalContext } from '@/context/tribal';
import {
  getCarConfigurationCode,
  getModelTrim,
  isNotBuildable,
  isStockCar,
} from '@/modules/utils/storeUtils';
import useGlobalStore, {
  type GlobalStoreStateConfiguration,
} from '@/stores/globalStore';
import Box from '../../Box/Box';
import CTADownloadBrochure from '../../CTADownloadBrochure/CTADownloadBrochure';
import FooterModel from '../FooterModel/FooterModel';
import { StyledFooterSelectionsGoTo } from './FooterSelections.styles';
import { SectionNames } from '@/analytics/sectionNames';
import { performVWBasicButtonClick } from '@/analytics/events';
import useAgentService from '@/hooks/useAgentService';

interface FooterSelectionsProps {
  activeConfiguration: GlobalStoreStateConfiguration;
  showComparisonView?: boolean;
}

const FooterSelections = ({
  activeConfiguration,
  showComparisonView,
}: FooterSelectionsProps) => {
  const { contentManager, analyticsManager, analyticsBuilder } =
    useTribalContext();

  const { configurations, retailers } = useGlobalStore((state) => state.app);
  const { getConfiguratorUrl } = useAgentService();

  const handleCarConfiguratorClick = (
    url: string,
    car: GlobalStoreStateConfiguration
  ) => {
    window.open(url, '_blank');
    performVWBasicButtonClick({
      label: contentManager.get('ctas-goto-configurator'),
      sectionName: SectionNames.FOOTER,
      configuration: car,
      retailers,
      analyticsManager,
      analyticsBuilder,
    });
  };

  const singleCarData = {
    code: `${contentManager.get(
      'configuration-code'
    )}: ${getCarConfigurationCode(activeConfiguration)}`,
    isStock: isStockCar(activeConfiguration),
    onClick: () => {
      const configuratorUrl = getConfiguratorUrl(activeConfiguration);
      handleCarConfiguratorClick(configuratorUrl, activeConfiguration);
    },
  };

  const multiCarData = configurations.map(
    (car: GlobalStoreStateConfiguration) => {
      if (isNotBuildable(car.pdbData)) {
        return {
          title: getModelTrim(car.modelTitle, car.trimTitle),
          description: car.pdbData?.derivative?.title,
          code: (
            <Text bold={false}>
              <CTADownloadBrochure sectionName={SectionNames.FOOTER} />
            </Text>
          ),
          onClick: () => {
            const configuratorUrl = getConfiguratorUrl(car);
            handleCarConfiguratorClick(configuratorUrl, car);
          },
        };
      } else {
        return {
          title:
            getModelTrim(
              car.pdbData?.derivative?.modelTitle || '',
              car.pdbData?.derivative?.trimTitle || ''
            ) || car.modelTitle,
          description: car.pdbData?.derivative?.title,
          code: `
          ${contentManager.get(
            'configuration-code'
          )}: ${getCarConfigurationCode(car)}`,
          isStock: isStockCar(car),
          onClick: () => {
            const configuratorUrl = getConfiguratorUrl(car);
            handleCarConfiguratorClick(configuratorUrl, car);
          },
        };
      }
    }
  );

  let updateYourSelectionsCopy = contentManager.get(
    'footer-update-description'
  );

  if (showComparisonView) {
    const hasStockCars = configurations.find((configuration) =>
      isStockCar(configuration)
    );

    if (hasStockCars) {
      updateYourSelectionsCopy = contentManager.get(
        'footer-update-description-compare-stock-car'
      );
    } else {
      updateYourSelectionsCopy = contentManager.get(
        'footer-update-description-compare'
      );
    }
  } else if (!showComparisonView && isStockCar(activeConfiguration)) {
    updateYourSelectionsCopy = contentManager.get(
      'footer-update-description-stock-car'
    );
  }

  return (
    <div>
      <Text tag={TextTag.h3} appearance={TokenTextAppearance.bigcopy200} bold>
        {contentManager.get('footer-update-title')}
      </Text>

      <Box
        as={Text}
        tag="p"
        appearance={TokenTextAppearance.copy200}
        mb={DesignTokenSizeEnum.static350}
      >
        {updateYourSelectionsCopy}
      </Box>
      <StyledFooterSelectionsGoTo>
        {showComparisonView ? (
          multiCarData.map((selection, idx) => (
            <FooterModel key={`${selection.title}-${idx}`} {...selection} />
          ))
        ) : (
          <FooterModel {...singleCarData} single />
        )}
      </StyledFooterSelectionsGoTo>
    </div>
  );
};

export default FooterSelections;
